/**
 * "확정 검토하려면" 접힘 — 표에 가르는 사실로 나오지 않은 나머지 예/아니오 확인(법적 배제 조건)을 접어 둔다.
 * 답하지 않아도 제안은 그대로이고, 확정하려는 사람만 펼쳐 답한다. "모두 맞습니다"는 질문별 `yes` 저장이다(표시 묶음일 뿐).
 */
import React, { useState } from "react";

const MUTED = "var(--audit-text-muted)"; const LINE = "var(--audit-border)"; const BLUE = "#1d4ed8";

export default function CandidateChecklistFold({ groups, disabled = false, onAnswer, onAnswerMany, narrowed = null, onOpenProve }) {
  const checklist = groups?.checklist || [];
  const inputs = groups?.inputs || [];
  const open = checklist.filter((c) => c.state === "open");
  const flagged = checklist.filter((c) => c.state === "flag");
  const ready = open.length === 0 && flagged.length === 0 && checklist.length > 0;
  if (!checklist.length && !inputs.length) return null;
  return (
    <details data-testid="checklist-fold" style={{ marginTop: 10, border: `1px solid ${LINE}`, borderRadius: 8, background: "var(--audit-bg-elevated, var(--audit-bg))" }}>
      <summary style={{ cursor: "pointer", padding: "8px 12px", fontSize: 12.5, color: BLUE, listStyle: "none" }}>
        ▸ {narrowed ? `${narrowed.slice(0, 4)}.${narrowed.slice(4, 6)}-${narrowed.slice(6)}으로 ` : ""}확정 검토하려면 남은 법적 확인 <b>{open.length}</b>개{flagged.length ? ` · 해당한다고 답한 항목 ${flagged.length}개` : ""}
        <span style={{ marginLeft: 8, fontSize: 11, color: MUTED }}>답하지 않아도 제안은 그대로입니다. 확정하려는 사람만 답합니다.</span>
      </summary>
      <div style={{ padding: "4px 12px 10px", fontSize: 12 }}>
        <ol style={{ margin: "4px 0 8px", paddingLeft: 18, color: MUTED, lineHeight: 1.55 }}>
          {checklist.map((c) => (
            <li key={c.key} data-testid="fold-check" data-state={c.state} style={{ color: c.state === "ok" ? "var(--audit-text)" : c.state === "flag" ? "var(--audit-text)" : MUTED }}>
              {c.state === "ok" ? "✓ " : c.state === "flag" ? "! " : ""}{c.prompt}
              {c.state === "flag" && <b style={{ marginLeft: 6, fontSize: 11.5 }}>아닙니다 · 현재 경로 밖일 수 있음</b>}
              {c.state !== "open" && <button type="button" disabled={disabled} onClick={() => onAnswer?.(c.key, null)} aria-label={`${c.prompt.slice(0, 20)} 답 지우기`} style={{ marginLeft: 6, border: 0, background: "none", color: MUTED, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>✕</button>}
              {c.state === "open" && <span style={{ marginLeft: 6, display: "inline-flex", gap: 4 }}>
                <button type="button" disabled={disabled} onClick={() => onAnswer?.(c.key, "no")} style={{ border: `1px solid ${LINE}`, borderRadius: 5, padding: "0 7px", fontSize: 11, background: "transparent", color: MUTED, cursor: "pointer", fontFamily: "inherit" }}>아닙니다</button>
              </span>}
            </li>
          ))}
        </ol>
        {open.length > 0 && (
          <button type="button" disabled={disabled} onClick={() => onAnswerMany?.(Object.fromEntries(open.map((c) => [c.key, "yes"])))} style={{ width: "100%", padding: 7, border: "1px solid #2563eb", color: BLUE, borderRadius: 6, fontWeight: 600, fontSize: 12, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>
            위 {open.length}개 모두 맞습니다 · 질문별로 저장
          </button>
        )}
        {ready && narrowed && onOpenProve && (
          <button type="button" onClick={() => onOpenProve()} style={{ width: "100%", marginTop: 6, padding: 8, border: "1px solid #2563eb", background: "#2563eb", color: "#fff", borderRadius: 6, fontWeight: 600, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>
            확정 검토 · 보고서
          </button>
        )}
        {inputs.length > 0 && (
          <div data-testid="checklist-inputs" style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 11.5, color: MUTED }}>설명 입력 질문 {inputs.length}개 — 글로 적어 저장하면 같은 키로 기록되어 다시 계산합니다.</div>
            {inputs.map((c) => <InputQuestion key={c.key} question={c} disabled={disabled} onAnswer={onAnswer} />)}
          </div>
        )}
      </div>
    </details>
  );
}

/** 글로 답하는 질문 한 줄 — 입력 + 저장. 비우고 저장하면 답을 지운다. */
function InputQuestion({ question, disabled, onAnswer }) {
  const [text, setText] = useState(question.answer || "");
  const dirty = text !== (question.answer || "");
  return (
    <form data-testid="checklist-input" data-key={question.key} onSubmit={(e) => { e.preventDefault(); onAnswer?.(question.key, text.trim() ? text.trim() : null); }} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <label htmlFor={`ci-${question.key}`} style={{ fontSize: 12, minWidth: 0, flex: "1 1 220px" }}>{question.prompt}{question.answer ? <span style={{ marginLeft: 6, color: "#1d4ed8", fontSize: 11 }}>✓ 답함</span> : null}</label>
      <input id={`ci-${question.key}`} value={text} onChange={(e) => setText(e.target.value)} disabled={disabled} maxLength={500} placeholder="설명을 적으세요"
        style={{ flex: "2 1 240px", minWidth: 0, border: `1px solid ${LINE}`, borderRadius: 6, padding: "5px 9px", font: "inherit", fontSize: 12.5, background: "transparent", color: "inherit" }} />
      <button type="submit" disabled={disabled || !dirty} style={{ border: "1px solid #2563eb", color: dirty ? "#fff" : MUTED, background: dirty ? "#2563eb" : "transparent", borderColor: dirty ? "#2563eb" : LINE, borderRadius: 6, padding: "4px 10px", fontSize: 12, fontFamily: "inherit", cursor: dirty ? "pointer" : "default" }}>저장</button>
    </form>
  );
}
